import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import ProfileHeader from '../components/profile/ProfileHeader';
import ProfileTabs from '../components/profile/ProfileTabs';
import PostsTab from '../components/profile/tabs/PostsTab';
import ReviewsTab from '../components/profile/tabs/ReviewsTab';
import AboutTab from '../components/profile/tabs/AboutTab';
import CoursesTab from '../components/profile/tabs/CoursesTab';
import PeopleCard from '../components/PeopleCard';
import './styles/TrainerConsole.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';
import { getTrainerById, updateTrainer, uploadTrainerPictures } from '../../api/TrainerAPI';
import { FILTER_OPTIONS } from '../data/mockPeople';

// Available options for modals
const AVAILABLE_SPECIALIZATIONS = [
    'Strength Training', 'HIIT', 'Yoga', 'Pilates', 'CrossFit', 'Cardio',
    'Weight Loss', 'Muscle Building', 'Flexibility', 'Sports Performance',
    'Rehabilitation', 'Nutrition Coaching', 'Prenatal Fitness', 'Senior Fitness',
    'Martial Arts', 'Boxing', 'Swimming', 'Running', 'Cycling', 'Sports Injuries',
    'Mobility Training', 'Functional Fitness', 'Posture Correction',
    'Endurance Training', 'Injury Prevention', 'Youth Athletic Development',
    'Athletic Conditioning'
];

const AVAILABLE_LANGUAGES = [
    'English', 'Afrikaans'
];

const TrainerConsole = () => {
    const { personId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Check if returning from course editor with tab state
    const initialTab = location.state?.tab || 'bookings';
    const [activeTab, setActiveTab] = useState(initialTab);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // Original data for comparison
    const [originalPerson, setOriginalPerson] = useState(null);
    const [person, setPerson] = useState(null);

    const [posts, setposts] = useState([]);
    const [reviews, setreviews] = useState([]);

    // Mock bookings data
    const [bookings] = useState([
        { id: 1, date: '2026-01-13', time: '09:00', duration: 60, client: 'Sarah M.', type: '1-on-1 Training' },
        { id: 2, date: '2026-01-13', time: '11:00', duration: 45, client: 'Mike T.', type: 'HIIT Session' },
        { id: 3, date: '2026-01-13', time: '14:30', duration: 60, client: 'Group Session', type: 'Group Training' },
        { id: 4, date: '2026-01-14', time: '10:00', duration: 60, client: 'Emma R.', type: '1-on-1 Training' },
        { id: 5, date: '2026-01-14', time: '15:00', duration: 90, client: 'David L.', type: 'Personal Program Review' },
        { id: 6, date: '2026-01-15', time: '08:00', duration: 60, client: 'Lisa K.', type: '1-on-1 Training' },
    ]);

    // Modal editing states
    const [showPostModal, setShowPostModal] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [editingService, setEditingService] = useState(null);

    // New modal states
    const [showQualificationModal, setShowQualificationModal] = useState(false);
    const [editingQualification, setEditingQualification] = useState(null);
    const [showCertificationModal, setShowCertificationModal] = useState(false);
    const [editingCertification, setEditingCertification] = useState(null);
    const [showSpecializationsModal, setShowSpecializationsModal] = useState(false);
    const [showLanguagesModal, setShowLanguagesModal] = useState(false);

    const fileInputRef = useRef(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(null);
    
    // Image preview and upload states
    const [photoPreviewUrls, setPhotoPreviewUrls] = useState({
        primaryPhoto: null,
        secondaryPhoto: null,
        coverPhoto: null
    });
    const [pendingPhotoFiles, setPendingPhotoFiles] = useState({
        primaryPhoto: null,
        secondaryPhoto: null,
        coverPhoto: null
    });
    const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
    
    // Loading and validation states
    const [isSaving, setIsSaving] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);

    useEffect(() => {
        getTrainerData(personId);
    }, [personId, navigate]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Create default empty trainer profile structure
    const createDefaultPerson = () => ({
        name: '',
        roles: [''],
        accolades: '',
        primaryPhoto: '',
        secondaryPhoto: '',
        coverPhoto: '',
        tags: [],
        hoverInfo: {
            bio: '',
            connectionReason: ''
        },
        profile: {
            bio: '',
            location: '',
            qualifications: [],
            certifications: [],
            specializations: [],
            languages: []
        },
        services: [],
        courses: [],
        rating: 0,
        reviewCount: 0
    });

    async function getTrainerData(id) {
        const res = await getTrainerById(id);
        if (res.success) {
            // Handle empty/null/undefined userData - treat as uninitialized profile
            const userData = res.userData;
            if (!userData || Object.keys(userData).length === 0) {
                // Profile not initialized - create default structure
                const defaultPerson = createDefaultPerson();
                setPerson(defaultPerson);
                setOriginalPerson(JSON.parse(JSON.stringify(defaultPerson)));
                setposts([]);
                setreviews([]);
            } else {
                // Ensure all required nested structures exist
                const safePerson = {
                    ...createDefaultPerson(),
                    ...userData,
                    profile: {
                        ...createDefaultPerson().profile,
                        ...(userData.profile || {})
                    },
                    hoverInfo: {
                        ...createDefaultPerson().hoverInfo,
                        ...(userData.hoverInfo || {})
                    },
                    services: userData.services || [],
                    courses: userData.courses || [],
                    tags: userData.tags || [],
                    roles: userData.roles || ['']
                };
                setPerson(safePerson);
                setOriginalPerson(JSON.parse(JSON.stringify(safePerson)));
                setposts(res.posts || []);
                setreviews(res.reviews || []);
            }
        } else {
            navigate('/connect');
        }
    }

    // Track if any changes have been made (including pending photo uploads)
    const hasChanges = useMemo(() => {
        if (!person || !originalPerson) return false;
        const dataChanged = JSON.stringify(person) !== JSON.stringify(originalPerson);
        const photosChanged = Object.values(pendingPhotoFiles).some(file => file !== null);
        return dataChanged || photosChanged;
    }, [person, originalPerson, pendingPhotoFiles]);

    // Compile all changes for save
    const compileChangesPayload = () => {
        if (!person || !originalPerson) return null;

        const payload = {};

        // Profile details
        if (person.name !== originalPerson.name) payload.name = person.name;
        if (person.roles?.join(',') !== originalPerson.roles?.join(',')) payload.roles = person.roles;
        if (person.accolades !== originalPerson.accolades) payload.accolades = person.accolades;

        // Photos
        if (person.primaryPhoto !== originalPerson.primaryPhoto) payload.primaryPhoto = person.primaryPhoto;
        if (person.secondaryPhoto !== originalPerson.secondaryPhoto) payload.secondaryPhoto = person.secondaryPhoto;
        if (person.coverPhoto !== originalPerson.coverPhoto) payload.coverPhoto = person.coverPhoto;

        // Tags
        if (JSON.stringify(person.tags) !== JSON.stringify(originalPerson.tags)) {
            payload.tags = person.tags;
        }

        // Hover Info
        if (person.hoverInfo) {
            const hoverInfoChanges = {};
            if (person.hoverInfo.bio !== originalPerson.hoverInfo?.bio) hoverInfoChanges.bio = person.hoverInfo.bio;
            if (person.hoverInfo.connectionReason !== originalPerson.hoverInfo?.connectionReason) {
                hoverInfoChanges.connectionReason = person.hoverInfo.connectionReason;
            }
            if (Object.keys(hoverInfoChanges).length > 0) payload.hoverInfo = hoverInfoChanges;
        }

        // Profile nested data
        if (person.profile) {
            const profileChanges = {};
            if (person.profile.bio !== originalPerson.profile?.bio) profileChanges.bio = person.profile.bio;
            if (person.profile.location !== originalPerson.profile?.location) profileChanges.location = person.profile.location;
            if (JSON.stringify(person.profile.qualifications) !== JSON.stringify(originalPerson.profile?.qualifications)) {
                profileChanges.qualifications = person.profile.qualifications;
            }
            if (JSON.stringify(person.profile.certifications) !== JSON.stringify(originalPerson.profile?.certifications)) {
                profileChanges.certifications = person.profile.certifications;
            }
            if (JSON.stringify(person.profile.specializations) !== JSON.stringify(originalPerson.profile?.specializations)) {
                profileChanges.specializations = person.profile.specializations;
            }
            if (JSON.stringify(person.profile.languages) !== JSON.stringify(originalPerson.profile?.languages)) {
                profileChanges.languages = person.profile.languages;
            }
            if (Object.keys(profileChanges).length > 0) payload.profile = profileChanges;
        }

        // Services
        if (JSON.stringify(person.services) !== JSON.stringify(originalPerson.services)) {
            payload.services = person.services;
        }

        // Courses
        if (JSON.stringify(person.courses) !== JSON.stringify(originalPerson.courses)) {
            payload.courses = person.courses;
        }

        return Object.keys(payload).length > 0 ? payload : null;
    };

    // Validation function for Save All
    const validateProfile = () => {
        const errors = [];

        // Required Profile Fields
        if (!person.profile?.bio?.trim()) {
            errors.push('Bio is required');
        }
        if (!person.profile?.specializations?.length) {
            errors.push('At least one specialization is required');
        }
        if (!person.profile?.languages?.length) {
            errors.push('At least one language is required');
        }

        // Required Additional Fields
        if (!person.accolades?.trim()) {
            errors.push('Accolades is required');
        }

        // Card Edit Tab Requirements
        if (!person.hoverInfo?.bio?.trim()) {
            errors.push('Hover bio (Card Edit) is required');
        }
        if (!person.hoverInfo?.connectionReason?.trim()) {
            errors.push('Connection reason (Card Edit) is required');
        }
        
        // Required images (check both stored URLs and pending uploads)
        if (!person.primaryPhoto && !pendingPhotoFiles.primaryPhoto) {
            errors.push('Primary photo is required');
        }
        if (!person.secondaryPhoto && !pendingPhotoFiles.secondaryPhoto) {
            errors.push('Secondary photo (hover state) is required');
        }

        // At least one service must exist
        if (!person.services?.length) {
            errors.push('At least one service is required');
        }

        return errors;
    };

    const handleSaveAll = async () => {
        // Clear previous validation errors
        setValidationErrors([]);

        // Run validation
        const errors = validateProfile();
        if (errors.length > 0) {
            setValidationErrors(errors);
            return;
        }

        setIsSaving(true);
        
        try {
            let uploadedPhotoUrls = {};
            
            // 1. Upload pending photos first and capture the returned URLs
            if (hasPendingPhotos) {
                const filesToUpload = [];
                Object.entries(pendingPhotoFiles).forEach(([photoType, file]) => {
                    if (file) {
                        filesToUpload.push({ file, staticName: photoType });
                    }
                });

                if (filesToUpload.length > 0) {
                    setIsUploadingPhotos(true);
                    try {
                        const results = await uploadTrainerPictures(filesToUpload, personId);
                        
                        // Capture uploaded URLs
                        results.forEach(({ name, url }) => {
                            uploadedPhotoUrls[name] = url;
                        });

                        // Update person state with new Firebase URLs
                        setPerson(prev => ({
                            ...prev,
                            ...uploadedPhotoUrls
                        }));

                        // Clear pending files and preview URLs
                        Object.keys(pendingPhotoFiles).forEach(key => {
                            if (photoPreviewUrls[key]) {
                                URL.revokeObjectURL(photoPreviewUrls[key]);
                            }
                        });
                        
                        setPhotoPreviewUrls({
                            primaryPhoto: null,
                            secondaryPhoto: null,
                            coverPhoto: null
                        });
                        setPendingPhotoFiles({
                            primaryPhoto: null,
                            secondaryPhoto: null,
                            coverPhoto: null
                        });
                    } catch (error) {
                        console.error('Error uploading photos:', error);
                        setValidationErrors(['Failed to upload photos. Please try again.']);
                        setIsSaving(false);
                        setIsUploadingPhotos(false);
                        return;
                    } finally {
                        setIsUploadingPhotos(false);
                    }
                }
            }

            // 2. Compile payload and merge with uploaded photo URLs
            const payload = compileChangesPayload() || {};
            
            // Merge uploaded photo URLs directly into payload (bypasses state timing issues)
            const finalPayload = { ...payload, ...uploadedPhotoUrls };
            
            if (Object.keys(finalPayload).length > 0) {
                console.log('Save All Payload:', finalPayload);
                const result = await updateTrainer(finalPayload, personId);
                if (result.success) {
                    console.log('Trainer updated successfully');
                    // Update originalPerson with new photo URLs
                    setOriginalPerson(prev => ({
                        ...JSON.parse(JSON.stringify(person)),
                        ...uploadedPhotoUrls
                    }));
                }
            }
        } catch (error) {
            console.error('Error saving trainer data:', error);
            setValidationErrors(['An error occurred while saving. Please try again.']);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDiscardChanges = () => {
        // Revoke all preview URLs
        Object.values(photoPreviewUrls).forEach(url => {
            if (url) URL.revokeObjectURL(url);
        });
        
        // Clear pending files and previews
        setPhotoPreviewUrls({
            primaryPhoto: null,
            secondaryPhoto: null,
            coverPhoto: null
        });
        setPendingPhotoFiles({
            primaryPhoto: null,
            secondaryPhoto: null,
            coverPhoto: null
        });
        
        // Reset person data
        setPerson(JSON.parse(JSON.stringify(originalPerson)));
    };

    // Direct field update handlers
    const updateField = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setPerson(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else if (field === 'roles') {
            setPerson(prev => ({
                ...prev,
                roles: [value]
            }));
        } else {
            setPerson(prev => ({ ...prev, [field]: value }));
        }
    };

    // Cleanup preview URLs on unmount
    useEffect(() => {
        return () => {
            // Revoke all object URLs on unmount
            Object.values(photoPreviewUrls).forEach(url => {
                if (url) URL.revokeObjectURL(url);
            });
        };
    }, []);

    // Photo upload handler - use object URLs for preview
    const handlePhotoClick = (photoType) => {
        setUploadingPhoto(photoType);
        fileInputRef.current?.click();
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files?.[0];
        if (file && uploadingPhoto) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                alert('Please select a valid image file (JPG, PNG, or WEBP)');
                setUploadingPhoto(null);
                return;
            }

            // Revoke old preview URL if exists
            if (photoPreviewUrls[uploadingPhoto]) {
                URL.revokeObjectURL(photoPreviewUrls[uploadingPhoto]);
            }

            // Create new preview URL
            const previewUrl = URL.createObjectURL(file);
            
            // Store preview URL and pending file
            setPhotoPreviewUrls(prev => ({
                ...prev,
                [uploadingPhoto]: previewUrl
            }));
            setPendingPhotoFiles(prev => ({
                ...prev,
                [uploadingPhoto]: file
            }));
        }
        setUploadingPhoto(null);
        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Remove a pending photo
    const handleRemovePhoto = (photoType) => {
        // Revoke preview URL
        if (photoPreviewUrls[photoType]) {
            URL.revokeObjectURL(photoPreviewUrls[photoType]);
        }
        
        setPhotoPreviewUrls(prev => ({
            ...prev,
            [photoType]: null
        }));
        setPendingPhotoFiles(prev => ({
            ...prev,
            [photoType]: null
        }));
    };

    // Get display URL for a photo (preview takes priority over stored URL)
    const getPhotoDisplayUrl = (photoType) => {
        return photoPreviewUrls[photoType] || person?.[photoType] || '';
    };

    // Check if there are pending photo uploads
    const hasPendingPhotos = Object.values(pendingPhotoFiles).some(file => file !== null);

    // Upload pending photos to Firebase
    const uploadPendingPhotos = async () => {
        const filesToUpload = [];
        
        Object.entries(pendingPhotoFiles).forEach(([photoType, file]) => {
            if (file) {
                filesToUpload.push({ file, staticName: photoType });
            }
        });

        if (filesToUpload.length === 0) return true;

        setIsUploadingPhotos(true);
        try {
            const results = await uploadTrainerPictures(filesToUpload, personId);
            
            // Update person state with new Firebase URLs
            const photoUpdates = {};
            results.forEach(({ name, url }) => {
                photoUpdates[name] = url;
            });

            setPerson(prev => ({
                ...prev,
                ...photoUpdates
            }));

            // Clear pending files and preview URLs
            Object.keys(pendingPhotoFiles).forEach(key => {
                if (photoPreviewUrls[key]) {
                    URL.revokeObjectURL(photoPreviewUrls[key]);
                }
            });
            
            setPhotoPreviewUrls({
                primaryPhoto: null,
                secondaryPhoto: null,
                coverPhoto: null
            });
            setPendingPhotoFiles({
                primaryPhoto: null,
                secondaryPhoto: null,
                coverPhoto: null
            });

            return true;
        } catch (error) {
            console.error('Error uploading photos:', error);
            return false;
        } finally {
            setIsUploadingPhotos(false);
        }
    };

    // Post handlers
    const openPostModal = (post = null) => {
        setEditingPost(post || { id: null, date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), content: '', likes: 0, comments: 0 });
        setShowPostModal(true);
    };

    const savePost = () => {
        if (editingPost.id) {
            setposts(prev => prev.map(p => p.id === editingPost.id ? editingPost : p));
        } else {
            setposts(prev => [...prev, { ...editingPost, id: Date.now() }]);
        }
        setShowPostModal(false);
        setEditingPost(null);
    };

    const deletePost = (postId) => {
        setposts(prev => prev.filter(p => p.id !== postId));
    };

    // Service handlers
    const openServiceModal = (service = null) => {
        setEditingService(service || { id: null, name: '', description: '', price: 0, period: 'session' });
        setShowServiceModal(true);
    };

    const saveService = () => {
        if (editingService.id) {
            setPerson(prev => ({
                ...prev,
                services: prev.services.map(s => s.id === editingService.id ? editingService : s)
            }));
        } else {
            setPerson(prev => ({
                ...prev,
                services: [...prev.services, { ...editingService, id: Date.now() }]
            }));
        }
        setShowServiceModal(false);
        setEditingService(null);
    };

    const deleteService = (serviceId) => {
        setPerson(prev => ({
            ...prev,
            services: prev.services.filter(s => s.id !== serviceId)
        }));
    };

    // Qualification handlers
    const openQualificationModal = (qual = null, index = null) => {
        setEditingQualification(qual ? { ...qual, index } : { title: '', institution: '', year: '', index: null });
        setShowQualificationModal(true);
    };

    const saveQualification = () => {
        setPerson(prev => {
            const quals = [...(prev.profile?.qualifications || [])];
            if (editingQualification.index !== null) {
                quals[editingQualification.index] = {
                    title: editingQualification.title,
                    institution: editingQualification.institution,
                    year: editingQualification.year
                };
            } else {
                quals.push({
                    title: editingQualification.title,
                    institution: editingQualification.institution,
                    year: editingQualification.year
                });
            }
            return { ...prev, profile: { ...prev.profile, qualifications: quals } };
        });
        setShowQualificationModal(false);
        setEditingQualification(null);
    };

    const deleteQualification = (index) => {
        setPerson(prev => ({
            ...prev,
            profile: {
                ...prev.profile,
                qualifications: prev.profile.qualifications.filter((_, i) => i !== index)
            }
        }));
    };

    // Certification handlers
    const openCertificationModal = (cert = null, index = null) => {
        setEditingCertification(cert ? { ...cert, index } : { title: '', institution: '', year: '', index: null });
        setShowCertificationModal(true);
    };

    const saveCertification = () => {
        setPerson(prev => {
            const certs = [...(prev.profile?.certifications || [])];
            if (editingCertification.index !== null) {
                certs[editingCertification.index] = {
                    title: editingCertification.title,
                    institution: editingCertification.institution,
                    year: editingCertification.year
                };
            } else {
                certs.push({
                    title: editingCertification.title,
                    institution: editingCertification.institution,
                    year: editingCertification.year
                });
            }
            return { ...prev, profile: { ...prev.profile, certifications: certs } };
        });
        setShowCertificationModal(false);
        setEditingCertification(null);
    };

    const deleteCertification = (index) => {
        setPerson(prev => ({
            ...prev,
            profile: {
                ...prev.profile,
                certifications: prev.profile.certifications.filter((_, i) => i !== index)
            }
        }));
    };


    // Specializations handlers (max 3)
    const toggleSpecialization = (spec) => {
        setPerson(prev => {
            const specs = [...(prev.profile?.specializations || [])];
            const index = specs.indexOf(spec);
            if (index > -1) {
                specs.splice(index, 1);
            } else if (specs.length < 3) {
                specs.push(spec);
            }
            return { ...prev, profile: { ...prev.profile, specializations: specs } };
        });
    };

    // Languages handlers
    const toggleLanguage = (lang) => {
        setPerson(prev => {
            const langs = [...(prev.profile?.languages || [])];
            const index = langs.indexOf(lang);
            if (index > -1) {
                langs.splice(index, 1);
            } else {
                langs.push(lang);
            }
            return { ...prev, profile: { ...prev.profile, languages: langs } };
        });
    };

    // Tags handlers
    const toggleTag = (tag) => {
        setPerson(prev => {
            const tags = [...(prev.tags || [])];
            const index = tags.indexOf(tag);
            if (index > -1) {
                tags.splice(index, 1);
            } else {
                tags.push(tag);
            }
            return { ...prev, tags };
        });
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    // Group bookings by date
    const groupedBookings = bookings.reduce((groups, booking) => {
        const date = booking.date;
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(booking);
        return groups;
    }, {});

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        if (date.toDateString() === today.toDateString()) return 'Today';
        if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

        return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    };

    if (!person) {
        return (
            <div className="profile-loading">
                <div className="profile-loading__spinner"></div>
            </div>
        );
    }

    const renderTabContent = () => {
        switch (activeTab) {
            case 'bookings':
                return (
                    <div className="console-bookings-tab">
                        {Object.keys(groupedBookings).length === 0 ? (
                            <div className="tab-empty">
                                <p>No upcoming bookings</p>
                            </div>
                        ) : (
                            Object.entries(groupedBookings).map(([date, dayBookings]) => (
                                <div key={date} className="console-bookings-day">
                                    <h3 className="console-bookings-date">{formatDate(date)}</h3>
                                    <div className="console-bookings-list">
                                        {dayBookings.map(booking => (
                                            <div key={booking.id} className="console-booking-card">
                                                <div className="console-booking-time">
                                                    <span className="console-booking-time-value">{booking.time}</span>
                                                    <span className="console-booking-duration">{booking.duration} min</span>
                                                </div>
                                                <div className="console-booking-details">
                                                    <span className="console-booking-client">{booking.client}</span>
                                                    <span className="console-booking-type">{booking.type}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                );
            case 'card-edit':
                return (
                    <div className="console-card-edit-tab">
                        {/* Card Previews Section - Side by Side */}
                        <section className="console-card-edit-section">
                            <h3 className="console-card-edit-section-title">Card Previews</h3>
                            <p className="console-card-edit-section-desc">Primary (default) and Secondary (hover state) card views</p>
                            <div className="console-card-previews-row">
                                <div className="console-card-preview-item">
                                    <span className="console-card-preview-label">Default State</span>
                                    <div className="console-card-photo-wrapper" onClick={() => handlePhotoClick('primaryPhoto')}>
                                        <PeopleCard 
                                            person={{
                                                ...person,
                                                primaryPhoto: getPhotoDisplayUrl('primaryPhoto'),
                                                secondaryPhoto: getPhotoDisplayUrl('secondaryPhoto')
                                            }} 
                                            onClick={() => { }} 
                                        />
                                        <div className="console-card-photo-overlay">
                                            <PhotoCameraIcon sx={{ fontSize: 24 }} />
                                            <span>Change Photo</span>
                                        </div>
                                        {pendingPhotoFiles.primaryPhoto && (
                                            <button 
                                                className="console-photo-remove-btn"
                                                onClick={(e) => { e.stopPropagation(); handleRemovePhoto('primaryPhoto'); }}
                                            >
                                                <CloseIcon sx={{ fontSize: 14 }} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="console-card-preview-item">
                                    <span className="console-card-preview-label">Hover State</span>
                                    <div className="console-card-photo-wrapper console-card-photo-wrapper--secondary" onClick={() => handlePhotoClick('secondaryPhoto')}>
                                        <div className="console-secondary-preview-card">
                                            <img
                                                src={getPhotoDisplayUrl('secondaryPhoto')}
                                                alt={`${person.name} - hover`}
                                                className="console-secondary-preview-image"
                                            />
                                            <div className="console-secondary-preview-content">
                                                <h3 className="console-secondary-preview-name">{person.name}</h3>
                                                <p className="console-secondary-preview-bio">{person.hoverInfo?.bio || ''}</p>
                                                <div className="console-secondary-preview-skills">
                                                    {(person.profile?.specializations || []).slice(0, 3).map((skill, index) => (
                                                        <span key={index} className="console-secondary-preview-skill">{skill}</span>
                                                    ))}
                                                </div>
                                                <p className="console-secondary-preview-reason">{person.hoverInfo?.connectionReason || ''}</p>
                                            </div>
                                        </div>
                                        <div className="console-card-photo-overlay">
                                            <PhotoCameraIcon sx={{ fontSize: 24 }} />
                                            <span>Change Photo</span>
                                        </div>
                                        {pendingPhotoFiles.secondaryPhoto && (
                                            <button 
                                                className="console-photo-remove-btn"
                                                onClick={(e) => { e.stopPropagation(); handleRemovePhoto('secondaryPhoto'); }}
                                            >
                                                <CloseIcon sx={{ fontSize: 14 }} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Hover Info Fields */}
                        <section className="console-card-edit-section">
                            <h3 className="console-card-edit-section-title">Hover Info</h3>
                            <p className="console-card-edit-section-desc">Text displayed when users hover over your card</p>
                            <div className="console-hover-info-fields">
                                <div className="console-hover-info-field">
                                    <label className="console-hover-info-label">Bio (Short)</label>
                                    <input
                                        type="text"
                                        className="console-inline-input"
                                        value={person.hoverInfo?.bio || ''}
                                        onChange={(e) => updateField('hoverInfo.bio', e.target.value)}
                                        placeholder="Short bio for hover state"
                                    />
                                </div>
                                <div className="console-hover-info-field">
                                    <label className="console-hover-info-label">Connection Reason</label>
                                    <input
                                        type="text"
                                        className="console-inline-input"
                                        value={person.hoverInfo?.connectionReason || ''}
                                        onChange={(e) => updateField('hoverInfo.connectionReason', e.target.value)}
                                        placeholder="Why should they connect with you?"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* Tags Section */}
                        <section className="console-card-edit-section">
                            <h3 className="console-card-edit-section-title">Tags</h3>
                            <p className="console-card-edit-section-desc">Filter tags that help users discover your profile</p>
                            <div className="console-tags-grid">
                                {FILTER_OPTIONS.map(tag => (
                                    <button
                                        key={tag}
                                        className={`console-tag-item ${(person.tags || []).includes(tag) ? 'console-tag-item--selected' : ''}`}
                                        onClick={() => toggleTag(tag)}
                                    >
                                        {tag}
                                        {(person.tags || []).includes(tag) && (
                                            <CheckIcon sx={{ fontSize: 14 }} />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </section>
                    </div>
                );
            case 'about':
                return (
                    <div className="console-about-tab">
                        <section className="about-section">
                            <h2 className="about-section__title">Bio</h2>
                            <textarea
                                className="console-inline-textarea"
                                value={person.profile?.bio || ''}
                                onChange={(e) => updateField('profile.bio', e.target.value)}
                                placeholder="Tell people about yourself..."
                                rows={4}
                            />
                        </section>

                        <section className="about-section">
                            <div className="console-section-header">
                                <h2 className="about-section__title">Qualifications</h2>
                                <button className="console-add-tag-btn" onClick={() => openQualificationModal()}>
                                    <AddIcon sx={{ fontSize: 14 }} /> Add
                                </button>
                            </div>
                            <div className="about-qualifications">
                                {(person.profile.qualifications || []).map((qual, index) => (
                                    <div key={index} className="about-qualification console-qualification-item">
                                        <div className="about-qualification__icon">🎓</div>
                                        <div className="about-qualification__content">
                                            <h3 className="about-qualification__title">{qual.title}</h3>
                                            <p className="about-qualification__meta">{qual.institution} • {qual.year}</p>
                                        </div>
                                        <div className="console-qualification-actions">
                                            <button className="console-edit-icon console-edit-icon--visible" onClick={() => openQualificationModal(qual, index)}>
                                                <EditIcon sx={{ fontSize: 14 }} />
                                            </button>
                                            <button className="console-edit-icon console-edit-icon--visible console-edit-icon--danger" onClick={() => deleteQualification(index)}>
                                                <DeleteIcon sx={{ fontSize: 14 }} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className="about-section">
                            <div className="console-section-header">
                                <h2 className="about-section__title">Certifications</h2>
                                <button className="console-add-tag-btn" onClick={() => openCertificationModal()}>
                                    <AddIcon sx={{ fontSize: 14 }} /> Add
                                </button>
                            </div>
                            <div className="about-qualifications">
                                {(person.profile.certifications || []).map((cert, index) => (
                                    <div key={index} className="about-qualification console-qualification-item">
                                        <div className="about-qualification__icon">📜</div>
                                        <div className="about-qualification__content">
                                            <h3 className="about-qualification__title">{cert.title}</h3>
                                            <p className="about-qualification__meta">{cert.institution} • {cert.year}</p>
                                        </div>
                                        <div className="console-qualification-actions">
                                            <button className="console-edit-icon console-edit-icon--visible" onClick={() => openCertificationModal(cert, index)}>
                                                <EditIcon sx={{ fontSize: 14 }} />
                                            </button>
                                            <button className="console-edit-icon console-edit-icon--visible console-edit-icon--danger" onClick={() => deleteCertification(index)}>
                                                <DeleteIcon sx={{ fontSize: 14 }} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {(!person.profile.certifications || person.profile.certifications.length === 0) && (
                                    <p className="console-empty-text">No certifications added yet</p>
                                )}
                            </div>
                        </section>

                        <section className="about-section">
                            <div className="console-section-header">
                                <h2 className="about-section__title">Specializations</h2>
                                <button className="console-add-tag-btn" onClick={() => setShowSpecializationsModal(true)}>
                                    <AddIcon sx={{ fontSize: 14 }} /> Edit
                                </button>
                            </div>
                            <div className="about-tags">
                                {(person.profile.specializations || []).map((spec, index) => (
                                    <span key={index} className="about-tag">{spec}</span>
                                ))}
                                {(!person.profile.specializations || person.profile.specializations.length === 0) && (
                                    <p className="console-empty-text">No specializations added yet</p>
                                )}
                            </div>
                        </section>

                        <section className="about-section">
                            <div className="console-section-header">
                                <h2 className="about-section__title">Languages</h2>
                                <button className="console-add-tag-btn" onClick={() => setShowLanguagesModal(true)}>
                                    <AddIcon sx={{ fontSize: 14 }} /> Edit
                                </button>
                            </div>
                            <div className="about-languages">
                                {(person.profile.languages || []).map((lang, index) => (
                                    <span key={index} className="about-language-tag">{lang}</span>
                                ))}
                                {(!person.profile.languages || person.profile.languages.length === 0) && (
                                    <p className="console-empty-text">No languages added yet</p>
                                )}
                            </div>
                        </section>

                        <section className="about-section">
                            <h2 className="about-section__title">Accolades</h2>
                            <input
                                type="text"
                                className="console-inline-input"
                                value={person.accolades || ''}
                                onChange={(e) => updateField('accolades', e.target.value)}
                                placeholder="e.g., 10+ years experience"
                            />
                        </section>
                    </div>
                );
            case 'posts':
                return (
                    <div className="console-posts-tab">
                        <div className="console-posts-header">
                            <button className="console-add-btn" onClick={() => openPostModal()}>
                                <AddIcon sx={{ fontSize: 18 }} />
                                Create Post
                            </button>
                        </div>
                        <div className="posts-tab">
                            {posts.map(post => (
                                <article key={post.id} className="post-card console-post-card">
                                    <div className="console-post-actions">
                                        <button className="console-action-btn" onClick={() => openPostModal(post)}>
                                            <EditIcon sx={{ fontSize: 16 }} />
                                        </button>
                                        <button className="console-action-btn console-action-btn--danger" onClick={() => deletePost(post.id)}>
                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                        </button>
                                    </div>
                                    <div className="post-card__header">
                                        <span className="post-card__date">{post.date}</span>
                                    </div>
                                    <p className="post-card__content">{post.content}</p>
                                    <div className="post-card__footer">
                                        <span className="post-card__stat">
                                            <span className="post-card__stat-icon">❤️</span>
                                            {post.likes}
                                        </span>
                                        <span className="post-card__stat">
                                            <span className="post-card__stat-icon">💬</span>
                                            {post.comments}
                                        </span>
                                    </div>
                                </article>
                            ))}
                            {posts.length === 0 && (
                                <div className="tab-empty">
                                    <p>No posts yet. Create your first post!</p>
                                </div>
                            )}
                        </div>
                    </div>
                );
            case 'reviews':
                return <ReviewsTab reviews={reviews} rating={person.rating} reviewCount={person.reviewCount} />;
            case 'courses':
                return (
                    <CoursesTab trainerId={personId} />
                );
            case 'services':
                return (
                    <div className="console-services-tab">
                        <div className="console-services-header">
                            <button className="console-add-btn" onClick={() => openServiceModal()}>
                                <AddIcon sx={{ fontSize: 18 }} />
                                Add Service
                            </button>
                        </div>
                        <div className="profile-services-list">
                            {person.services.map(service => (
                                <div key={service.id} className="profile-service-item console-service-item">
                                    <div className="profile-service-info">
                                        <span className="profile-service-name">{service.name}</span>
                                        <span className="profile-service-desc">{service.description}</span>
                                    </div>
                                    <div className="profile-service-price">
                                        <span className="profile-service-amount">R{service.price}</span>
                                        <span className="profile-service-period">/{service.period}</span>
                                    </div>
                                    <div className="console-service-actions">
                                        <button className="console-action-btn" onClick={() => openServiceModal(service)}>
                                            <EditIcon sx={{ fontSize: 16 }} />
                                        </button>
                                        <button className="console-action-btn console-action-btn--danger" onClick={() => deleteService(service.id)}>
                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            default:
                return <AboutTab person={person} />;
        }
    };

    return (
        <div className="profile-page trainer-console">
            <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoChange}
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
            />

            {/* Validation Errors Alert */}
            {validationErrors.length > 0 && (
                <div className="console-validation-errors">
                    <div className="console-validation-errors__header">
                        <span>Please fix the following issues before saving:</span>
                        <button className="console-validation-errors__close" onClick={() => setValidationErrors([])}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                        </button>
                    </div>
                    <ul className="console-validation-errors__list">
                        {validationErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Sticky Save Bar */}
            {hasChanges && (
                <div className="console-save-bar">
                    <div className="console-save-bar__content">
                        <span className="console-save-bar__text">
                            {isUploadingPhotos ? 'Uploading photos...' : 'You have unsaved changes'}
                        </span>
                        <div className="console-save-bar__actions">
                            <button className="console-save-bar__btn console-save-bar__btn--discard" onClick={handleDiscardChanges} disabled={isSaving || isUploadingPhotos}>
                                <CloseIcon sx={{ fontSize: 16 }} />
                                Discard
                            </button>
                            <button className="console-save-bar__btn console-save-bar__btn--save" onClick={handleSaveAll} disabled={isSaving || isUploadingPhotos}>
                                {(isSaving || isUploadingPhotos) ? (
                                    <>
                                        <div className="console-save-spinner"></div>
                                        {isUploadingPhotos ? 'Uploading...' : 'Saving...'}
                                    </>
                                ) : (
                                    <>
                                        <SaveIcon sx={{ fontSize: 16 }} />
                                        Save All
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="profile-container">
                <div className="profile-header-wrapper">
                    <button className="profile-back-btn" onClick={() => navigate('/connect')}>
                        <ArrowBackIcon />
                    </button>

                    {/* Editable Profile Header */}
                    <div className="profile-header console-header">
                        <div
                            className="profile-header__cover console-cover-editable"
                            style={{ backgroundImage: `url(${getPhotoDisplayUrl('coverPhoto')})` }}
                            onClick={() => handlePhotoClick('coverPhoto')}
                        >
                            <div className="console-photo-overlay">
                                <PhotoCameraIcon sx={{ fontSize: 24 }} />
                                <span>Change Cover</span>
                            </div>
                            {pendingPhotoFiles.coverPhoto && (
                                <button 
                                    className="console-photo-remove-btn"
                                    onClick={(e) => { e.stopPropagation(); handleRemovePhoto('coverPhoto'); }}
                                >
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                </button>
                            )}
                        </div>

                        <div className="profile-header__content">
                            <div className="profile-header__avatar console-avatar-editable" onClick={() => handlePhotoClick('primaryPhoto')}>
                                <img src={getPhotoDisplayUrl('primaryPhoto')} alt={person.name} loading="lazy" />
                                <div className="console-photo-overlay console-photo-overlay--avatar">
                                    <PhotoCameraIcon sx={{ fontSize: 20 }} />
                                </div>
                                {pendingPhotoFiles.primaryPhoto && (
                                    <button 
                                        className="console-photo-remove-btn console-photo-remove-btn--avatar"
                                        onClick={(e) => { e.stopPropagation(); handleRemovePhoto('primaryPhoto'); }}
                                    >
                                        <CloseIcon sx={{ fontSize: 12 }} />
                                    </button>
                                )}
                            </div>

                            <div className="profile-header__info">
                                <div className="profile-header__name-row">
                                    <input
                                        type="text"
                                        className="console-inline-input console-inline-input--name"
                                        value={person.name || ''}
                                        onChange={(e) => updateField('name', e.target.value)}
                                        placeholder="Your name"
                                    />
                                    <span className="profile-header__verified-badge">✓ Verified</span>
                                </div>

                                <input
                                    type="text"
                                    className="console-inline-input console-inline-input--roles"
                                    value={person.roles[0] || ''}
                                    onChange={(e) => updateField('roles', e.target.value)}
                                    placeholder="e.g., Personal Trainer, Nutritionist"
                                />

                                <div className="profile-header__location" style={{ marginLeft: '12px' }}>
                                    <svg className="profile-header__location-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                    </svg>
                                    <input
                                        type="text"
                                        className="console-inline-input console-inline-input--location"
                                        value={person.profile?.location || ''}
                                        onChange={(e) => updateField('profile.location', e.target.value)}
                                        placeholder="Your location"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="profile-content">
                    <div className="profile-main">
                        <ProfileTabs
                            isConsole={true}
                            activeTab={activeTab}
                            onTabChange={handleTabChange}
                            isMobile={isMobile}
                        />
                        <div className="profile-tab-content">
                            {renderTabContent()}
                        </div>
                    </div>

                    <aside className="profile-sidebar">
                        <div className="profile-services-card">
                            <div className="console-sidebar-header">
                                <h3>Services</h3>
                                <button className="console-add-btn console-add-btn--small" onClick={() => openServiceModal()}>
                                    <AddIcon sx={{ fontSize: 16 }} />
                                </button>
                            </div>
                            <div className="profile-services-list">
                                {person.services.map(service => (
                                    <div key={service.id} className="profile-service-item" onClick={() => openServiceModal(service)}>
                                        <div className="profile-service-info">
                                            <span className="profile-service-name">{service.name}</span>
                                            <span className="profile-service-desc">{service.description}</span>
                                        </div>
                                        <div className="profile-service-price">
                                            <span className="profile-service-amount">R{service.price}</span>
                                            <span className="profile-service-period">/{service.period}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="profile-credentials-card">
                            <h3>Credentials</h3>
                            <div className="profile-credentials-list">
                                {person.profile?.qualifications?.slice(0, 3).map((qual, index) => (
                                    <div key={index} className="profile-credential-item">
                                        <span className="profile-credential-title">{qual.title}</span>
                                        <span className="profile-credential-meta">{qual.institution} • {qual.year}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            {/* Post Modal */}
            {showPostModal && (
                <div className="console-modal-overlay" onClick={() => setShowPostModal(false)}>
                    <div className="console-modal" onClick={e => e.stopPropagation()}>
                        <h3 className="console-modal__title">{editingPost?.id ? 'Edit Post' : 'Create Post'}</h3>
                        <textarea
                            className="console-modal__textarea"
                            value={editingPost?.content || ''}
                            onChange={(e) => setEditingPost(prev => ({ ...prev, content: e.target.value }))}
                            placeholder="What's on your mind?"
                            rows={5}
                        />
                        <div className="console-modal__actions">
                            <button className="console-modal__btn console-modal__btn--cancel" onClick={() => setShowPostModal(false)}>
                                Cancel
                            </button>
                            <button className="console-modal__btn console-modal__btn--save" onClick={savePost}>
                                {editingPost?.id ? 'Save Changes' : 'Create Post'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Service Modal */}
            {showServiceModal && (
                <div className="console-modal-overlay" onClick={() => setShowServiceModal(false)}>
                    <div className="console-modal console-modal--service" onClick={e => e.stopPropagation()}>
                        <h3 className="console-modal__title">{editingService?.id ? 'Edit Service' : 'Add Service'}</h3>
                        <div className="console-modal__form">
                            <div className="console-modal__field">
                                <label>Service Name</label>
                                <input
                                    type="text"
                                    value={editingService?.name || ''}
                                    onChange={(e) => setEditingService(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="e.g., 1-on-1 Training"
                                />
                            </div>
                            <div className="console-modal__field">
                                <label>Description</label>
                                <input
                                    type="text"
                                    value={editingService?.description || ''}
                                    onChange={(e) => setEditingService(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Brief description"
                                />
                            </div>
                            <div className="console-modal__row">
                                <div className="console-modal__field">
                                    <label>Price (R)</label>
                                    <input
                                        type="number"
                                        value={editingService?.price ?? ""}
                                        onChange={(e) =>
                                            setEditingService(prev => ({
                                                ...prev,
                                                price: e.target.value === "" ? "" : Number(e.target.value)
                                            }))
                                        }
                                    />
                                </div>
                                <div className="console-modal__field">
                                    <label>Period</label>
                                    <select
                                        value={editingService?.period || 'session'}
                                        onChange={(e) => setEditingService(prev => ({ ...prev, period: e.target.value }))}
                                    >
                                        <option value="session">Per Session</option>
                                        <option value="week">Per Week</option>
                                        <option value="month">Per Month</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="console-modal__actions">
                            <button className="console-modal__btn console-modal__btn--cancel" onClick={() => setShowServiceModal(false)}>
                                Cancel
                            </button>
                            <button className="console-modal__btn console-modal__btn--save" onClick={saveService}>
                                {editingService?.id ? 'Save Changes' : 'Add Service'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Qualification Modal */}
            {showQualificationModal && (
                <div className="console-modal-overlay" onClick={() => setShowQualificationModal(false)}>
                    <div className="console-modal" onClick={e => e.stopPropagation()}>
                        <h3 className="console-modal__title">{editingQualification?.index !== null ? 'Edit Qualification' : 'Add Qualification'}</h3>
                        <div className="console-modal__form">
                            <div className="console-modal__field">
                                <label>Title / Degree</label>
                                <input
                                    type="text"
                                    value={editingQualification?.title || ''}
                                    onChange={(e) => setEditingQualification(prev => ({ ...prev, title: e.target.value }))}
                                    placeholder="e.g., BSc in Sports Science"
                                />
                            </div>
                            <div className="console-modal__field">
                                <label>Institution</label>
                                <input
                                    type="text"
                                    value={editingQualification?.institution || ''}
                                    onChange={(e) => setEditingQualification(prev => ({ ...prev, institution: e.target.value }))}
                                    placeholder="e.g., University of Cape Town"
                                />
                            </div>
                            <div className="console-modal__field">
                                <label>Year</label>
                                <input
                                    type="text"
                                    value={editingQualification?.year || ''}
                                    onChange={(e) => setEditingQualification(prev => ({ ...prev, year: e.target.value }))}
                                    placeholder="e.g., 2020"
                                />
                            </div>
                        </div>
                        <div className="console-modal__actions">
                            <button className="console-modal__btn console-modal__btn--cancel" onClick={() => setShowQualificationModal(false)}>
                                Cancel
                            </button>
                            <button className="console-modal__btn console-modal__btn--save" onClick={saveQualification}>
                                {editingQualification?.index !== null ? 'Save Changes' : 'Add Qualification'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Certification Modal */}
            {showCertificationModal && (
                <div className="console-modal-overlay" onClick={() => setShowCertificationModal(false)}>
                    <div className="console-modal" onClick={e => e.stopPropagation()}>
                        <h3 className="console-modal__title">{editingCertification?.index !== null ? 'Edit Certification' : 'Add Certification'}</h3>
                        <div className="console-modal__form">
                            <div className="console-modal__field">
                                <label>Certification Name</label>
                                <input
                                    type="text"
                                    value={editingCertification?.title || ''}
                                    onChange={(e) => setEditingCertification(prev => ({ ...prev, title: e.target.value }))}
                                    placeholder="e.g., Certified Personal Trainer"
                                />
                            </div>
                            <div className="console-modal__field">
                                <label>Issuing Organization</label>
                                <input
                                    type="text"
                                    value={editingCertification?.institution || ''}
                                    onChange={(e) => setEditingCertification(prev => ({ ...prev, institution: e.target.value }))}
                                    placeholder="e.g., NASM"
                                />
                            </div>
                            <div className="console-modal__field">
                                <label>Year Obtained</label>
                                <input
                                    type="text"
                                    value={editingCertification?.year || ''}
                                    onChange={(e) => setEditingCertification(prev => ({ ...prev, year: e.target.value }))}
                                    placeholder="e.g., 2021"
                                />
                            </div>
                        </div>
                        <div className="console-modal__actions">
                            <button className="console-modal__btn console-modal__btn--cancel" onClick={() => setShowCertificationModal(false)}>
                                Cancel
                            </button>
                            <button className="console-modal__btn console-modal__btn--save" onClick={saveCertification}>
                                {editingCertification?.index !== null ? 'Save Changes' : 'Add Certification'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Specializations Modal */}
            {showSpecializationsModal && (
                <div className="console-modal-overlay" onClick={() => setShowSpecializationsModal(false)}>
                    <div className="console-modal console-modal--selection" onClick={e => e.stopPropagation()}>
                        <h3 className="console-modal__title">Edit Specializations</h3>
                        <p className="console-modal__subtitle">Select your areas of expertise ({(person.profile.specializations || []).length}/3)</p>
                        <div className="console-selection-grid">
                            {AVAILABLE_SPECIALIZATIONS.map(spec => {
                                const isSelected = (person.profile.specializations || []).includes(spec);
                                const isAtLimit = (person.profile.specializations || []).length >= 3;
                                const isDisabled = !isSelected && isAtLimit;

                                return (
                                    <button
                                        key={spec}
                                        className={`console-selection-item ${isSelected ? 'console-selection-item--selected' : ''} ${isDisabled ? 'console-selection-item--disabled' : ''}`}
                                        onClick={() => toggleSpecialization(spec)}
                                        disabled={isDisabled}
                                    >
                                        {spec}
                                        {isSelected && <CheckIcon sx={{ fontSize: 14 }} />}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="console-modal__actions">
                            <button className="console-modal__btn console-modal__btn--save" onClick={() => setShowSpecializationsModal(false)}>
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Languages Modal */}
            {showLanguagesModal && (
                <div className="console-modal-overlay" onClick={() => setShowLanguagesModal(false)}>
                    <div className="console-modal console-modal--selection" onClick={e => e.stopPropagation()}>
                        <h3 className="console-modal__title">Edit Languages</h3>
                        <p className="console-modal__subtitle">Select languages you speak</p>
                        <div className="console-selection-grid">
                            {AVAILABLE_LANGUAGES.map(lang => (
                                <button
                                    key={lang}
                                    className={`console-selection-item ${(person.profile.languages || []).includes(lang) ? 'console-selection-item--selected' : ''}`}
                                    onClick={() => toggleLanguage(lang)}
                                >
                                    {lang}
                                    {(person.profile.languages || []).includes(lang) && (
                                        <CheckIcon sx={{ fontSize: 14 }} />
                                    )}
                                </button>
                            ))}
                        </div>
                        <div className="console-modal__actions">
                            <button className="console-modal__btn console-modal__btn--save" onClick={() => setShowLanguagesModal(false)}>
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrainerConsole;
